import { useTranslation } from "react-i18next";

export const DAY_COUNT = 7;

export const DEFAULT_RANGE = { opens_at: "09:00", closes_at: "17:00" };

/** A whole week, every day present, nothing open. The shape the editor edits. */
export const emptyWeek = () =>
  Array.from({ length: DAY_COUNT }, (_, day) => ({
    day,
    open: false,
    ranges: [{ ...DEFAULT_RANGE }],
  }));

/** API rows -> one entry per weekday, so every day renders whether open or not. */
export const toWeek = (rows) =>
  Array.from({ length: DAY_COUNT }, (_, day) => {
    const ranges = (rows ?? [])
      .filter((r) => r.day === day)
      .map((r) => ({ opens_at: r.opens_at, closes_at: r.closes_at }));

    return { day, open: ranges.length > 0, ranges: ranges.length ? ranges : [{ ...DEFAULT_RANGE }] };
  });

/** Week state -> the flat list the API expects; closed days contribute nothing. */
export const toRows = (week) =>
  week.filter((d) => d.open).flatMap((d) => d.ranges.map((r) => ({ day: d.day, ...r })));

/** How many days are open. Zero is what both callers refuse to submit. */
export const openDayCount = (week) => week.filter((d) => d.open).length;

/**
 * The seven-day, several-ranges-per-day editor itself — controlled, and knowing
 * nothing about where its week is going.
 *
 * It was inside `WorkingHours`, which owned the state, the API call and the
 * card around it. The create-branch form needs the same grid without any of
 * that: its hours are submitted with the branch, not saved on their own. So the
 * grid moved here and `WorkingHours` became one of its two callers rather than
 * the editor being rebuilt a second time.
 */
export default function WorkingHoursEditor({ week, onChange, disabled = false, errors = {} }) {
  const { t } = useTranslation();

  const update = (day, changes) =>
    onChange(week.map((d) => (d.day === day ? { ...d, ...changes } : d)));

  const updateRange = (day, index, field, value) =>
    onChange(
      week.map((d) =>
        d.day === day
          ? { ...d, ranges: d.ranges.map((r, i) => (i === index ? { ...r, [field]: value } : r)) }
          : d
      )
    );

  const addRange = (day) =>
    onChange(
      week.map((d) => (d.day === day ? { ...d, ranges: [...d.ranges, { ...DEFAULT_RANGE }] } : d))
    );

  const removeRange = (day, index) =>
    onChange(
      week.map((d) =>
        d.day === day && d.ranges.length > 1
          ? { ...d, ranges: d.ranges.filter((_, i) => i !== index) }
          : d
      )
    );

  /**
   * Most salons keep one set of hours six or seven days a week, so typing them
   * out seven times is the slowest part of setting a branch up. This copies one
   * day's ranges onto every *other* day and opens them — closed days included,
   * because "same as Monday" is the whole point of pressing it. A day the user
   * then wants shut is one checkbox away.
   */
  const copyToAll = (day) => {
    const source = week.find((d) => d.day === day);
    if (!source) return;

    onChange(
      week.map((d) =>
        d.day === day
          ? d
          : { ...d, open: true, ranges: source.ranges.map((r) => ({ ...r })) }
      )
    );
  };

  /** The API reports a bad row as working_hours.3.opens_at — show the first. */
  const rowError = (day) => {
    const rows = toRows(week);
    const index = rows.findIndex((r) => r.day === day);
    if (index === -1) return undefined;

    return (
      errors[`working_hours.${index}.opens_at`]?.[0] ??
      errors[`working_hours.${index}.closes_at`]?.[0] ??
      errors[`working_hours.${index}.day`]?.[0]
    );
  };

  return (
    <div className="divide-y divide-line">
      {week.map((d) => (
        <div key={d.day} className="flex flex-wrap items-start gap-3 py-3">
          <div className="w-28 flex-shrink-0 pt-2 text-sm font-medium">{t(`days.${d.day}`)}</div>

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="checkbox"
                  checked={d.open}
                  disabled={disabled}
                  onChange={(e) => update(d.day, { open: e.target.checked })}
                  className="h-4 w-4 rounded border-line-strong"
                />
                {t("workingHours.open")}
              </label>

              {d.open && (
                <button
                  type="button"
                  onClick={() => copyToAll(d.day)}
                  disabled={disabled}
                  className="rounded px-2 py-1 text-xs font-medium text-accent-text hover:bg-accent-wash disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2"
                >
                  {t("workingHours.copyToAll")}
                </button>
              )}
            </div>

            {d.open &&
              d.ranges.map((range, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <input
                    type="time"
                    dir="ltr"
                    value={range.opens_at}
                    disabled={disabled}
                    onChange={(e) => updateRange(d.day, index, "opens_at", e.target.value)}
                    className="field-input w-32"
                  />
                  <span className="text-ink-muted">–</span>
                  <input
                    type="time"
                    dir="ltr"
                    value={range.closes_at}
                    disabled={disabled}
                    onChange={(e) => updateRange(d.day, index, "closes_at", e.target.value)}
                    className="field-input w-32"
                  />
                  {d.ranges.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRange(d.day, index)}
                      disabled={disabled}
                      aria-label={t("workingHours.removeRange")}
                      className="rounded px-2 py-1 text-sm text-danger-text hover:bg-danger-wash"
                    >
                      ✕
                    </button>
                  )}
                  {index === d.ranges.length - 1 && (
                    <button
                      type="button"
                      onClick={() => addRange(d.day)}
                      disabled={disabled}
                      className="rounded px-2 py-1 text-xs font-medium text-accent-text hover:bg-accent-wash"
                    >
                      {t("workingHours.addRange")}
                    </button>
                  )}
                </div>
              ))}

            {rowError(d.day) && <p className="field-error">{rowError(d.day)}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
